import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Gamepad2, RefreshCw, Users, TrendingUp, TrendingDown, DollarSign,
} from 'lucide-react';
import DateWindowPicker from '../../components/DateWindowPicker';

/**
 * GameAnalyticsPage — iter 132.
 *
 * Per-game load/redeem totals over a selectable window. "Points used"
 * in each game = total $ loaded by all players. Sourced from our own
 * wallet_ledger (reliable + instant) — no dependency on upstream game
 * records APIs that half the providers don't expose.
 */
export default function GameAnalyticsPage() {
  const [window, setWindow] = useState({ window: 'today' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (window.window) params.set('window', window.window);
      if (window.start) params.set('start', window.start);
      if (window.end) params.set('end', window.end);
      const r = await api.get(`/api/admin/game-analytics?${params}`);
      setData(r.data);
    } catch {
      toast.error('Failed to load game analytics');
    } finally {
      setLoading(false);
    }
  }, [window]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="p-6 space-y-6" data-testid="admin-game-analytics-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Gamepad2 className="w-6 h-6 text-indigo-400" />
            Game Usage Analytics
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            How much $ each game takes in (loads) vs pays out (redeems) per window. Net = house profit per game.
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          className="border-white/10"
          data-testid="game-analytics-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <DateWindowPicker value={window} onChange={setWindow} testidPrefix="game-window" />

      {loading && !data ? (
        <div className="text-gray-400 text-sm py-10 text-center">Loading…</div>
      ) : data ? (
        <>
          <div className="grid md:grid-cols-5 gap-3">
            <Kpi label="Total Loaded" value={`$${data.kpis.total_loaded.toLocaleString()}`}
                 color="#22c55e" icon={<DollarSign className="w-4 h-4" />}
                 testid="kpi-game-loaded" />
            <Kpi label="Total Redeemed" value={`$${data.kpis.total_redeemed.toLocaleString()}`}
                 color="#f59e0b" icon={<TrendingDown className="w-4 h-4" />}
                 testid="kpi-game-redeemed" />
            <Kpi label="Net (House)"
                 value={`${data.kpis.net >= 0 ? '+' : ''}$${data.kpis.net.toLocaleString()}`}
                 color={data.kpis.net >= 0 ? '#22c55e' : '#ef4444'}
                 icon={<TrendingUp className="w-4 h-4" />}
                 testid="kpi-game-net" />
            <Kpi label="Loads" value={data.kpis.total_load_count.toLocaleString()}
                 color="#6366f1" icon={<Gamepad2 className="w-4 h-4" />}
                 testid="kpi-game-load-count" />
            <Kpi label="Top Game" value={data.kpis.top_game || '—'}
                 color="#D4AF37" icon={<Gamepad2 className="w-4 h-4" />}
                 testid="kpi-game-top" />
          </div>

          <div className="rounded-2xl p-5 space-y-3 border border-white/5"
               style={{ background: 'rgba(255,255,255,0.02)' }}
               data-testid="game-table-section">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-white">By Game</h2>
              <span className="text-xs text-gray-500">
                {data.kpis.unique_players} unique player{data.kpis.unique_players === 1 ? '' : 's'}
              </span>
            </div>

            {data.per_game.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center" data-testid="game-empty">
                No game activity in this window.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                      <th className="text-left py-2 px-2">Game</th>
                      <th className="text-right py-2 px-2">Loaded</th>
                      <th className="text-right py-2 px-2">Redeemed</th>
                      <th className="text-right py-2 px-2">Net</th>
                      <th className="text-right py-2 px-2">Loads</th>
                      <th className="text-right py-2 px-2">Redeems</th>
                      <th className="text-right py-2 px-2">Avg Load</th>
                      <th className="text-right py-2 px-2">Players</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_game.map((g) => (
                      <tr key={g.game_id}
                          className="border-b border-white/5 hover:bg-white/5"
                          data-testid={`game-row-${g.game_id}`}>
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-2">
                            {g.logo_url && (
                              <img src={g.logo_url} alt="" className="w-6 h-6 rounded object-contain bg-black/30" />
                            )}
                            <span className="font-semibold text-white">{g.game_name}</span>
                            {!g.enabled && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">disabled</span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-green-400 font-bold">
                          ${g.loaded.toLocaleString()}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-amber-400">
                          ${g.redeemed.toLocaleString()}
                        </td>
                        <td className={`py-2 px-2 text-right tabular-nums font-bold ${g.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {g.net >= 0 ? '+' : ''}${g.net.toLocaleString()}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-gray-300">{g.load_count}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-gray-400">{g.redeem_count}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-indigo-300">${g.avg_load}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-purple-300">{g.unique_players}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function Kpi({ label, value, color, icon, testid }) {
  return (
    <div
      className="rounded-xl p-3 border"
      style={{ background: '#131822', borderColor: `${color}33` }}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        <span style={{ color }}>{icon}</span>
        {label}
      </div>
      <div className="text-xl font-bold mt-1 truncate" style={{ color }}>{value}</div>
    </div>
  );
}
